(() => {
  'use strict';
  if (!window.customElements || customElements.get('fs-popup-menu')) return;

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  const conflictsSelector = 'dialog[open], cart-drawer.active, cart-notification.active, .cart-notification.active, details-modal details[open], header-drawer > details[open], modal-dialog[open], quick-add-modal[open], [role="dialog"][aria-modal="true"]';

  class FsPopupMenu extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.header = this.closest('.header');
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-fs-menu-trigger]');
      this.closeButton = this.querySelector('[data-fs-menu-close]');
      this.scroller = this.querySelector('[data-fs-menu-scroll]');
      if (!this.header || !this.dialog || !this.trigger || !this.closeButton || typeof this.dialog.showModal !== 'function') return;
      if (getComputedStyle(this).getPropertyValue('--fs-menu-styles-ready').trim() !== '1') return;
      if (this.header.querySelector('header-drawer > details[open]')) return;
      this.connected = true;
      this.onTrigger = () => this.dialog.open ? this.close() : this.open();
      this.onPointerDown = (event) => { this.backdropStart = event.target === this.dialog && this.outside(event); };
      this.onPointerCancel = () => { this.backdropStart = false; };
      this.onClick = (event) => {
        if (event.target.closest('[data-fs-menu-close]')) this.close();
        else if (event.target === this.dialog && this.backdropStart && this.outside(event)) this.close();
        else if (event.target.closest('a[href]') && !event.defaultPrevented) this.close(false);
        this.backdropStart = false;
      };
      this.onCancel = (event) => { event.preventDefault(); this.close(); };
      this.onClose = () => { if (!this.dialog.open) this.finishClose(); };
      this.onKeyDown = (event) => this.trapFocus(event);
      this.onHistory = () => this.close();
      this.onPageHide = () => this.close(false);
      this.onViewport = () => this.updateViewport();
      this.trigger.addEventListener('click', this.onTrigger);
      this.dialog.addEventListener('pointerdown', this.onPointerDown);
      this.dialog.addEventListener('pointercancel', this.onPointerCancel);
      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('popstate', this.onHistory);
      window.addEventListener('pagehide', this.onPageHide);
      this.hidden = false;
      this.header.classList.add('fs-popup-menu-ready');
      this.watchStickyTimer();
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.close(false);
      this.finishClose();
      this.stopWatchingStickyTimer();
      this.trigger.removeEventListener('click', this.onTrigger);
      this.dialog.removeEventListener('pointerdown', this.onPointerDown);
      this.dialog.removeEventListener('pointercancel', this.onPointerCancel);
      this.dialog.removeEventListener('click', this.onClick);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('popstate', this.onHistory);
      window.removeEventListener('pagehide', this.onPageHide);
      this.header.classList.remove('fs-popup-menu-ready');
      this.hidden = true;
      this.connected = false;
    }

    watchStickyTimer() {
      this.stickySection = this.closest('.section-header');
      if (!this.stickySection) return;
      this.stickySelector = '.gta-widget.gta-bar.GSC-BAR-kWHCocIrolnI';
      this.stickyPrevious = {
        value: this.stickySection.style.getPropertyValue('--fs-menu-sticky-offset'),
        priority: this.stickySection.style.getPropertyPriority('--fs-menu-sticky-offset')
      };
      this.scheduleStickyTimer = () => {
        if (this.stickyFrame != null) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = null;
          if (this.connected) this.updateStickyTimer();
        });
      };
      if (typeof ResizeObserver === 'function') this.stickyResizeObserver = new ResizeObserver(this.scheduleStickyTimer);
      if (typeof MutationObserver === 'function') {
        this.stickyMutationObserver = new MutationObserver((records) => {
          const relevant = records.some((record) => {
            if (record.type === 'attributes') return this.stickyTimer && (record.target === this.stickyTimer || record.target.contains(this.stickyTimer));
            return [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 && (node === this.stickyTimer || node.contains(this.stickyTimer) || node.matches(this.stickySelector) || node.querySelector(this.stickySelector)));
          });
          if (relevant) this.scheduleStickyTimer();
        });
        this.stickyMutationObserver.observe(this.ownerDocument.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'style', 'aria-hidden'] });
      }
      window.addEventListener('resize', this.scheduleStickyTimer, { passive: true });
      window.addEventListener('scroll', this.scheduleStickyTimer, { passive: true });
      this.updateStickyTimer();
    }

    updateStickyTimer() {
      const timer = this.ownerDocument.querySelector(this.stickySelector);
      if (timer !== this.stickyTimer) {
        this.stickyResizeObserver?.disconnect();
        this.stickyTimer = timer;
        if (timer) this.stickyResizeObserver?.observe(timer);
      }
      let offset = 0;
      if (timer && this.visible(timer)) {
        const style = getComputedStyle(timer);
        const rect = timer.getBoundingClientRect();
        if ((style.position === 'sticky' || style.position === 'fixed') && rect.bottom > 0 && rect.top < window.innerHeight) {
          offset = Math.max(0, Math.min(rect.height, rect.bottom, window.innerHeight));
        }
      }
      const value = `${Math.ceil(offset)}px`;
      if (this.stickySection.style.getPropertyValue('--fs-menu-sticky-offset') !== value) this.stickySection.style.setProperty('--fs-menu-sticky-offset', value);
      this.stickyApplied = value;
    }

    stopWatchingStickyTimer() {
      if (!this.stickySection) return;
      this.stickyResizeObserver?.disconnect();
      this.stickyMutationObserver?.disconnect();
      if (this.stickyFrame != null) cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = null;
      window.removeEventListener('resize', this.scheduleStickyTimer);
      window.removeEventListener('scroll', this.scheduleStickyTimer);
      if (this.stickySection.style.getPropertyValue('--fs-menu-sticky-offset') === this.stickyApplied) {
        if (this.stickyPrevious.value) this.stickySection.style.setProperty('--fs-menu-sticky-offset', this.stickyPrevious.value, this.stickyPrevious.priority);
        else this.stickySection.style.removeProperty('--fs-menu-sticky-offset');
      }
      this.stickyTimer = null;
      this.stickySection = null;
    }

    visible(node) {
      if (!node || node.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      if (node.closest('dialog:not([open]), modal-dialog:not([open]), quick-add-modal:not([open]), product-modal:not([open]), cart-drawer:not(.active), .cart-notification:not(.active)')) return false;
      for (let ancestor = node.parentElement; ancestor; ancestor = ancestor.parentElement) {
        if (ancestor.tagName !== 'DETAILS' || ancestor.hasAttribute('open')) continue;
        const summary = Array.from(ancestor.children).find((child) => child.tagName === 'SUMMARY');
        if (!summary?.contains(node)) return false;
      }
      const style = getComputedStyle(node);
      return style.display !== 'none' && style.visibility !== 'hidden' && node.getClientRects().length > 0;
    }

    conflictingModal() {
      return Array.from(this.ownerDocument.querySelectorAll(conflictsSelector)).find((node) => node !== this.dialog && !this.dialog.contains(node) && this.visible(node));
    }

    open() {
      if (!this.connected || this.dialog.open || this.conflictingModal()) return false;
      this.opener = this.ownerDocument.activeElement === this.ownerDocument.body ? this.trigger : this.ownerDocument.activeElement;
      this.restoreFocus = true;
      this.openSession = true;
      this.backdropStart = false;
      this.updateViewport();
      this.lockScroll();
      try {
        this.dialog.showModal();
      } catch (_) {
        this.finishClose();
        this.header.classList.remove('fs-popup-menu-ready');
        this.hidden = true;
        this.header.querySelector('header-drawer > details > summary, .header__inline-menu a')?.focus({ preventScroll: true });
        return false;
      }
      this.trigger.setAttribute('aria-expanded', 'true');
      if (this.scroller) this.scroller.scrollTop = 0;
      this.closeButton.focus({ preventScroll: true });
      window.addEventListener('resize', this.onViewport, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onViewport, { passive: true });
      window.visualViewport?.addEventListener('scroll', this.onViewport, { passive: true });
      if (typeof MutationObserver === 'function') {
        this.conflictObserver = new MutationObserver(() => {
          if (this.dialog.open && this.conflictingModal()) this.close(false);
        });
        this.conflictObserver.observe(this.ownerDocument.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['open', 'class', 'hidden', 'aria-hidden', 'aria-modal'] });
      }
      return true;
    }

    close(restoreFocus = true) {
      this.restoreFocus = restoreFocus;
      if (this.dialog?.open) this.dialog.close();
      this.finishClose();
    }

    finishClose() {
      if (!this.openSession) return;
      this.openSession = false;
      this.conflictObserver?.disconnect();
      this.conflictObserver = null;
      window.removeEventListener('resize', this.onViewport);
      window.visualViewport?.removeEventListener('resize', this.onViewport);
      window.visualViewport?.removeEventListener('scroll', this.onViewport);
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.backdropStart = false;
      this.unlockScroll();
      const opener = this.opener;
      this.opener = null;
      if (this.restoreFocus !== false && !this.conflictingModal()) {
        const target = opener?.isConnected && this.visible(opener) ? opener : this.trigger;
        if (target?.isConnected && this.visible(target)) target.focus({ preventScroll: true });
      }
    }

    outside(event) {
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || !this.dialog.open) return;
      const nodes = Array.from(this.dialog.querySelectorAll(focusableSelector)).filter((node) => node.getAttribute('tabindex') !== '-1' && this.visible(node));
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = this.ownerDocument.activeElement;
      if (!first) { event.preventDefault(); this.dialog.focus({ preventScroll: true }); return; }
      if (event.shiftKey && (active === first || !nodes.includes(active))) { event.preventDefault(); last.focus({ preventScroll: true }); }
      else if (!event.shiftKey && (active === last || !nodes.includes(active))) { event.preventDefault(); first.focus({ preventScroll: true }); }
    }

    updateViewport() {
      const viewport = window.visualViewport;
      this.dialog.style.setProperty('--fs-menu-viewport-height', `${Math.round(viewport?.height || window.innerHeight)}px`);
      this.dialog.style.setProperty('--fs-menu-viewport-top', `${Math.round(viewport?.offsetTop || 0)}px`);
    }

    lockScroll() {
      if (this.scrollState) return;
      const root = this.ownerDocument.documentElement;
      const body = this.ownerDocument.body;
      const gap = Math.max(0, window.innerWidth - root.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      this.scrollState = { x: window.scrollX || 0, y: window.scrollY || 0, properties: [] };
      const values = { '--fs-menu-scroll-top': `${-this.scrollState.y}px`, '--fs-menu-scroll-left': `${-this.scrollState.x}px`, '--fs-menu-body-padding': `${padding + gap}px` };
      Object.entries(values).forEach(([name, value]) => {
        this.scrollState.properties.push({ name, value: root.style.getPropertyValue(name), priority: root.style.getPropertyPriority(name), applied: value });
        root.style.setProperty(name, value);
      });
      root.classList.add('fs-popup-menu-locked');
    }

    unlockScroll() {
      if (!this.scrollState) return;
      const root = this.ownerDocument.documentElement;
      const { x, y, properties } = this.scrollState;
      this.scrollState = null;
      root.classList.remove('fs-popup-menu-locked');
      properties.forEach(({ name, value, priority, applied }) => {
        if (root.style.getPropertyValue(name) !== applied) return;
        if (value) root.style.setProperty(name, value, priority);
        else root.style.removeProperty(name);
      });
      const behavior = root.style.getPropertyValue('scroll-behavior');
      const priority = root.style.getPropertyPriority('scroll-behavior');
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo({ left: x, top: y, behavior: 'instant' });
      if (behavior) root.style.setProperty('scroll-behavior', behavior, priority);
      else root.style.removeProperty('scroll-behavior');
    }
  }

  customElements.define('fs-popup-menu', FsPopupMenu);
})();
